import React, { useEffect, useState } from "react";
import { FaArrowRight, FaEnvelope } from "react-icons/fa6";
import PageHero from "../components/PageHero";

export default function Contact() {
  useEffect(() => {
    document.title = 'Contact Us — Learning Outcomes Academy';
    return () => { document.title = 'Learning Outcomes Academy'; };
  }, []);
  return (
    <div className="w-full">
      <PageHero
        eyebrow="Get in touch"
        title={
          <>
            Tell us what you're working on —{" "}
            <span className="italic text-brand-deep">we'll meet you there.</span>
          </>
        }
        lead="Whether you're looking for a course, planning a workshop, preparing for accreditation, or considering a program development partnership, we'd love to hear from you."
      />

      <section className="bg-bone">
        <div className="container-page py-16 lg:py-24 grid grid-cols-1 lg:grid-cols-12 gap-10">
          <div className="lg:col-span-5 space-y-6">
            <div className="rounded-2xl bg-white border border-ink/10 p-6">
              <div className="eyebrow">By email</div>
              <p className="mt-3 text-ink/85 leading-relaxed">
                The fastest way to reach us is the form on this page. We typically
                respond within 1–2 business days.
              </p>
              <div className="mt-4 inline-flex items-center gap-2 text-sm text-slate2">
                <FaEnvelope className="text-brand-deep" />
                <span>Use the form to send us a note.</span>
              </div>
            </div>

            <div className="rounded-2xl bg-cream border border-ink/10 p-6">
              <div className="eyebrow">Inquiries we welcome</div>
              <ul className="mt-3 space-y-2 text-ink/85 text-sm">
                <li>• General questions and feedback</li>
                <li>• Course enrollment and custom cohorts</li>
                <li>• Research workshops for teams or departments</li>
                <li>• Accreditation preparation and consultations</li>
                <li>• Program development partnerships</li>
                <li>• Future tool early-access and pilot interest</li>
                <li>• Facilitator applications</li>
                <li>• Privacy policy questions</li>
                <li>• Terms of service questions</li>
              </ul>
            </div>
          </div>

          <div className="lg:col-span-7">
            <ContactForm />
          </div>
        </div>
      </section>
    </div>
  );
}

const ContactForm = () => {
  const [data, setData] = useState({ name: "", email: "", topic: "General inquiry", message: "" });

  const onChange = (e) => setData({ ...data, [e.target.name]: e.target.value });

  const submit = (e) => {
    e.preventDefault();
    const subject = encodeURIComponent(`[${data.topic}] Inquiry from ${data.name || "website"}`);
    const body = encodeURIComponent(
      `Name: ${data.name}\nEmail: ${data.email}\nTopic: ${data.topic}\n\n${data.message}`
    );
    window.location.href = `mailto:support@learningoutcomes.academy?subject=${subject}&body=${body}`;
  };

  const baseInput =
    "w-full rounded-xl border border-ink/15 bg-white px-4 py-3 text-ink placeholder:text-slate2/70 focus:outline-none focus:border-brand-deep focus:ring-2 focus:ring-brand-deep/20 transition";

  return (
    <form
      onSubmit={submit}
      className="rounded-3xl bg-white border border-ink/10 p-8 lg:p-10 shadow-soft"
    >
      <div className="eyebrow eyebrow-bar">Send us a note</div>
      <h2 className="mt-5 font-display text-2xl lg:text-3xl leading-tight text-ink">
        Tell us a little about what you're trying to accomplish.
      </h2>

      <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label className="text-sm text-slate2" htmlFor="name">Your name</label>
          <input id="name" name="name" value={data.name} onChange={onChange} className={`${baseInput} mt-1`} placeholder="Jane Doe" required />
        </div>
        <div>
          <label className="text-sm text-slate2" htmlFor="email">Email</label>
          <input id="email" type="email" name="email" value={data.email} onChange={onChange} className={`${baseInput} mt-1`} placeholder="you@example.com" required />
        </div>
      </div>

      <div className="mt-4">
        <label className="text-sm text-slate2" htmlFor="topic">Topic</label>
        <select id="topic" name="topic" value={data.topic} onChange={onChange} className={`${baseInput} mt-1`}>
          <option>General inquiry</option>
          <option>Course enrollment</option>
          <option>Research workshop</option>
          <option>Accreditation preparation</option>
          <option>Program development</option>
          <option>Tool early access</option>
          <option>Facilitator application</option>
          <option>Partnership</option>
          <option>Privacy policy</option>
          <option>Terms of service</option>
        </select>
      </div>

      <div className="mt-4">
        <label className="text-sm text-slate2" htmlFor="message">Message</label>
        <textarea id="message" name="message" value={data.message} onChange={onChange} rows={6} className={`${baseInput} mt-1 resize-y`} placeholder="A few sentences about what you're working on…" required />
      </div>

      <div className="mt-7 flex flex-wrap items-center justify-between gap-4">
        <p className="text-sm text-slate2">
          Submitting opens your email client to send the message directly to us.
        </p>
        <button type="submit" className="btn-primary">
          Send message <FaArrowRight className="text-xs" />
        </button>
      </div>
    </form>
  );
};
